import { useEffect, useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import GrowthCurves from './GrowthCurves';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllReadOnlyTrackingRecords,
  getReadOnlyAnalyticsUser,
} from '../../actions/trackingRecordsActions';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import React from 'react';
import { RELATIVE_PAGE_PATHS } from '../../utils/constants';
import SleepAnalytics from './SleepAnalytics';
import { ITrackerRecord } from '../../interfaces/ITrackerRecord.interface';
import { IUser } from '../../interfaces/IUser.interface';
import FeedingAnalytics from './FeedingAnalytics';
import { DateProvider } from './components/DateProvider';
import ManualLoader from '../common/ManualLoader';
import { isTokenInLocalStorage, showErrorModal } from '../../actions';
import navigationUtils from '../../utils/navigationUtils';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function getQueryType(index: number) {
  switch (index) {
    case 0:
      return 'growth';
    case 1:
      return 'feeding';
    case 2:
      return 'sleep';
    default:
      return '';
  }
}

function getQueryTab(type: string) {
  switch (type) {
    case 'growth':
      return 0;
    case 'feeding':
      return 1;
    case 'sleep':
      return 2;
    default:
      return 0;
  }
}

function normalizeEncryptedUserId(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

const AnalyticsReadOnlyPage = () => {
  const [tab, setTab] = useState(0);
  const [initialLoad, setInitialLoad] = useState(false);
  const [isTrackerDataLoaded, setIsTrackerDataLoaded] = useState(false);
  //@ts-ignore
  const loader = useSelector((state) => state.loader);
  const currentUser: IUser | null = useSelector(
    (state: any) => state.authentication.userData
  );
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const trackerRecords: ITrackerRecord[] = useSelector(
    (state: any) => state.trackingRecords
  );

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
    const params = new URLSearchParams(location.search);
    const encryptedUserId = normalizeEncryptedUserId(
      params.get('user-id') || ''
    );
    const token = params.get('token') || '';

    history.push(
      `${RELATIVE_PAGE_PATHS.ANALYTICS_READ_ONLY}?user-id=${encodeURIComponent(
        encryptedUserId
      )}&token=${encodeURIComponent(token)}&type=${getQueryType(newValue)}`
    );
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (isTokenInLocalStorage()) {
      const message = t(
        'stickers:journalScreen:logOutToAccessSharedJournalMessage'
      );
      dispatch(showErrorModal(message));
      navigationUtils.navigate(RELATIVE_PAGE_PATHS.STICKERS_HOME);
      return;
    }

    if (!initialLoad) {
      const params = new URLSearchParams(location.search);
      const encryptedUserId = normalizeEncryptedUserId(
        params.get('user-id') || ''
      );
      const token = params.get('token');

      if (encryptedUserId && token) {
        const fetchData = async () => {
          const recordsLoaded = await dispatch(
            //@ts-ignore
            getAllReadOnlyTrackingRecords(encryptedUserId, token, signal)
          );
          const userLoaded = await dispatch(
            //@ts-ignore
            getReadOnlyAnalyticsUser(encryptedUserId, token)
          );

          const isLoaded = Boolean(recordsLoaded && userLoaded);
          if (!isLoaded) {
            navigationUtils.navigate(RELATIVE_PAGE_PATHS.NOT_FOUND);
            return;
          }

          setIsTrackerDataLoaded(true);
        };

        fetchData();
      } else {
        navigationUtils.navigate(RELATIVE_PAGE_PATHS.NOT_FOUND);
      }
      setInitialLoad(true);
    }

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const type = searchParams.get('type');
    setTab(getQueryTab(type || ''));
  }, [location.search]);

  if (!isTrackerDataLoaded || loader || !currentUser?.childName) {
    return <ManualLoader />;
  }

  return (
    <div
      style={{
        marginBottom: '85px',
        width: '-webkit-fill-available',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Box id="tabs-sticky">
          <Tabs
            id="tabs-wrapper"
            value={tab}
            onChange={handleChange}
            aria-label="basic tabs example"
            centered
          >
            <Tab
              label={t('analytics:shared.growth')}
              className="tab-section"
              {...a11yProps(0)}
            />
            <Tab
              label={t('analytics:shared.feeding')}
              className="tab-section"
              {...a11yProps(1)}
            />
            <Tab
              label={t('analytics:shared.sleep')}
              className="tab-section"
              {...a11yProps(2)}
            />
          </Tabs>
        </Box>
        <CustomTabPanel value={tab} index={0}>
          <GrowthCurves
            trackerRecords={trackerRecords}
            currentUser={currentUser}
            disableTrackerLinks={true}
          />
        </CustomTabPanel>
        <DateProvider>
          <CustomTabPanel value={tab} index={1}>
            <FeedingAnalytics
              trackerRecords={trackerRecords}
              currentUser={currentUser}
            />
          </CustomTabPanel>
          <CustomTabPanel value={tab} index={2}>
            <SleepAnalytics
              trackerRecords={trackerRecords}
              currentUser={currentUser}
              disableTrackerLinks={true}
            />
          </CustomTabPanel>
        </DateProvider>
      </Box>
    </div>
  );
};

export default AnalyticsReadOnlyPage;

interface TabPanelProps {
  children?: JSX.Element;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}
